import { Layout, Menu, Spin, Badge, message, Tooltip, Input, Drawer } from 'antd';
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { makeRequestStateless } from '@utils/helpers';
import { getNavigationReq } from '@mods/common/requests';
import SubMenu from 'antd/es/menu/SubMenu';
import { encryptLocalData, decryptLocalData } from '@comps/commonFunctions';
import './navigation.css';
import { countNavNotificationsReq, getRolePermissionsReq } from './requests';
import { pusherInitializer } from '@comps/commonFunctions';
import { PlusOutlined, MenuFoldOutlined, MenuUnfoldOutlined, BulbOutlined, MoonOutlined } from '@ant-design/icons';
import { MinimizeOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilterOptions } from '../../../redux/filterOptionsSlice';

const langKey = 'navigation';
const { Sider } = Layout;
const DEFAULT_NAV_ICON = '/sidebaricon/List.svg';
const REQUEST_GROUP_MENU_KEY_SUFFIX = '__requestsGroup';
const APPROVAL_GROUP_MENU_KEY_SUFFIX = '__approvalsGroup';
const REQUEST_GROUP_MODULE_KEYS = [
  'companyLeaveRequest',
  'companyRelaxation',
  'companyExpenses',
  'companyLends',
  'companyOfficialDuty',
  'companyTravels',
  'companyAttendances',
  'companyOvertimes',
  'companyTransfers',
];
const GROUP_TYPES = {
  requests: 'requests',
  approvals: 'approvals',
};
const GROUPED_MENU_ICON_MAP = {
  companyLeaveRequest: 'leaveRequest',
  companyRelaxation: 'relaxation',
  companyExpenses: 'expenseRequests',
  companyLends: 'lendRequests',
  companyOfficialDuty: 'officialDutyRequests',
  companyTravels: 'travelRequests',
  companyAttendances: 'attendanceRequests',
  companyOvertimes: 'overtimes',
  companyTransfers: 'transfers',
  leaveApprovals: 'leaveApprovals',
  leaveCancelApprovals: 'leaveCancelApprovals',
  relaxationApprovals: 'relaxationApprovals',
  relaxationCancelApprovals: 'relaxationCancelApprovals',
  expenseApprovals: 'expenseApprovals',
  lendApprovals: 'lendApprovals',
  officialDutyApprovals: 'officialDutyApprovals',
  travelApprovals: 'travelApprovals',
  attendanceApprovals: 'attendanceApprovals',
  overtimeApprovals: 'overtimeApprovals',
  transferApprovals: 'transferApprovals',
  promotionApprovals: 'promotionApprovals',
  probationApprovals: 'probationApprovals',
  'case-sheet-approvals': 'casesheetapprovals',
};

const NavSide = (props) => {
  const hasReportedReadyRef = useRef(false);
  const searchModuleRef = useRef(null);
  const intl = useIntl();
  const {
    isMobileViewport = false,
    mobileNavOpen = false,
    setMobileNavOpen,
  } = props;

  const [menuFilter, setMenuFilter] = useState('');
  const authUser = useSelector((state) => state.auth.authUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [navThemeMode, setNavThemeMode] = useState(localStorage.getItem('navThemeMode') || 'dark');

  const [navigation, setNavigation] = useState({});
  const theme = 'light';
  const [current, setCurrent] = useState(localStorage.getItem('selectedMenuItem') || '');
  const [currentModuleKey, setModuleKey] = useState(localStorage.getItem('modulekey') || getLastSubstring(location.pathname));
  const [openKeys, setOpenKeys] = useState(
    JSON.parse(localStorage.getItem('openSubMenuKeys')) || ['']
  );
  const [notificationModules, setNotificationsModules] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const dispatch = useDispatch();

  const normalizeNotificationKey = useCallback((value) => {
    if (value === null || value === undefined) return "";

    return String(value)
      .replace(/^app\./, "")
      .replace(/[_\s./|-]/g, "")
      .toLowerCase();
  }, []);

  const getNotificationValue = (module) => {
    const count = module?.value ?? module?.count ?? module?.total ?? module?.notification_count ?? 0;
    const parsedCount = Number(count);

    return Number.isNaN(parsedCount) ? 0 : parsedCount;
  };

  const getNotificationLookupKeys = useCallback(
    (item) =>
      [
        item?.module_key,
        item?.moduleKey,
        item?.nav_key,
        item?.key,
        item?.name,
        item?.code,
        item?.sref,
        item?.identifier,
        item?.tag,
        item?.tags,
      ]
        .map(normalizeNotificationKey)
        .filter(Boolean),
    [normalizeNotificationKey]
  );

  const getNavItemLookupKeys = useCallback(
    (item) =>
      [
        getModuleKey(item),
        item?.module_key,
        item?.name,
        item?.code,
        item?.sref,
        item?.identifier,
        item?.tag,
        item?.tags,
      ]
        .map(normalizeNotificationKey)
        .filter(Boolean),
    [normalizeNotificationKey]
  );

  // ====================== OPTIMIZED NOTIFICATION COUNT (pure functions - no global mutable var) ======================
  const getNotificationCount = useCallback((modules, targetKeys) => {
    if (!modules || modules.length === 0) return 0;

    const lookupKeys = Array.isArray(targetKeys) ? targetKeys : [targetKeys].filter(Boolean);
    let total = 0;

    for (const module of modules) {
      const moduleKeys = getNotificationLookupKeys(module);
      const directMatch = moduleKeys.some((key) => lookupKeys.includes(key));

      if (directMatch) {
        total += getNotificationValue(module);
      }

      if (module.sub_modules?.length > 0) {
        total += getNotificationCount(module.sub_modules, lookupKeys);
      }
    }

    return total;
  }, [getNotificationLookupKeys]);

  const getNavNotificationCount = useCallback(
    (item) => {
      if (item?.syntheticGroup && item.children?.length > 0) {
        return item.children.reduce((total, child) => total + getNavNotificationCount(child), 0);
      }

      return getNotificationCount(notificationModules, getNavItemLookupKeys(item));
    },
    [notificationModules, getNotificationCount, getNavItemLookupKeys]
  );

  const countNotify = useCallback((item) => {
    const count = getNavNotificationCount(item);
    if (!count) return null;

    return (
      <Badge
        className="nav-notification-badge"
        size="small"
        count={ count }
        overflowCount={ 99 }
        color="#FF4D4F"
      />
    );
  }, [getNavNotificationCount]);

  // ====================== KEYDOWN & RESIZE (fixed - was broken in original) ======================
  useEffect(() => {
    // Ctrl + M to focus quick search
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'm') {
        event.preventDefault();
        searchModuleRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Resize handler (now properly inside effect)
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Initial resize
    handleResize();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Set collapsed based on width
  useEffect(() => {
    setCollapsed(windowWidth <= 829);
  }, [windowWidth]);

  // ====================== LOAD NAVIGATION (only on mount + pusher events) ======================
  const getNavWithPermissions = useCallback(() => {
    const nav = decryptLocalData('navigation');
    if (nav) {
      if (authUser?.super_admin) {
        nav.permissions.push(getReportsPermissions());
      }

      setNavigation(nav);
      setLoading(false);
    } else {
      setLoading(true);
      makeRequestStateless(getNavigationReq, null, onGetNavigation, null);
    }
  }, [authUser]);

  // Initial load only (critical performance fix - removed [current, openKeys] dependency)
  useEffect(() => {
    getNavWithPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && !hasReportedReadyRef.current) {
      hasReportedReadyRef.current = true;
      props.onReady?.();
    }
  }, [loading, props]);

  // ====================== NOTIFICATIONS ======================
  useEffect(() => {
    getNavNotificationsCount('notifications');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pusher for notifications
  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user?.tenant) return;

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    channel.bind('refresh-navigation-event', () => {
      getNavNotificationsCount('notifications');
    });

    return () => {
      channel.unbind('refresh-navigation-event');
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser]);

  // Pusher for role permissions update
  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user?.tenant) return;

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    channel.bind('RolePermissionsUpdatedEvent', (data) => {
      if (data && Number(data.role_id) === authUser?.role_id) {
        localStorage.setItem('navigation', '');
        message.info('Role Permissions Updated!');
        getRolePermissions(authUser.role_id);
        getNavWithPermissions();
      }
    });

    return () => {
      channel.unbind('RolePermissionsUpdatedEvent');
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [authUser]);

  const getRolePermissions = (roleId) => {
    setLoading(true);
    makeRequestStateless(getRolePermissionsReq, roleId, onGetPermissions, null);
  };

  const onGetPermissions = (data) => {
    encryptLocalData('permissions', data);
    setLoading(false);
  };

  const getNavNotificationsCount = (navKey) => {
    makeRequestStateless(countNavNotificationsReq, navKey, onSuccessNavNotifications, null);
  };

  const onSuccessNavNotifications = (data, res) => {
    const modules = Array.isArray(data)
      ? data
      : data?.sub_modules || data?.data?.sub_modules || res?.data?.sub_modules || [];
    setNotificationsModules(modules);
  };

  const getUrl = (sref) => {
    let url = sref ? sref.replace(/^app\./, '').replace(/_/g, '-').replace(/\./g, '/') : '';
    if (url === "masters/general/accolades") url = url.replace(/\/accolades/g, '');
    if (url === "masters/system/users") url = url.replace(/\/users/g, '');
    if (url === "payrollmanagement/payrolls/index") url = url.replace(/\/index/g, '');
    if (url === 'dashboard') url = '';
    return url;
  };

  // ====================== REPORTS FOR SUPER ADMIN ======================
  const getReportsPermissions = () => ({
    sref: "app.reports.all_reports",
    module_key: "reports",
    name: "REPORTS",
    icon: "fa fa-bar-chart",
    tag: null,
    id: 169,
    hasPermisson: 1,
    parentId: null,
    sort_order: 233,
    super_admin: 0,
    hide_in_nav: 0,
    is_mobile: 0,
    role_id: 4,
    identifier: "_"
  });

  const onGetNavigation = (data, res) => {
    setLoading(false);
    encryptLocalData('navigation', res.data);
    if (authUser?.super_admin) {
      res.data.permissions.push(getReportsPermissions());
    }
    setNavigation(res.data);
  };

  // ====================== TRANSLATE & ICON ======================
  const translateNav = useCallback((item) => {
    if (item?.code !== undefined) return item?.name || '';

    const translationKey = `${langKey}.${String(item?.module_key || '').replace(/-/g, "")}`;
    const translatedText = intl.messages?.[translationKey]
      ? intl.formatMessage({ id: translationKey })
      : '';

    return translatedText || item?.name || item?.module_key || '';
  }, [intl]);

  const renderSvgNavIcon = (key) => (
    <img
      src={ `/sidebaricon/${key}.svg` }
      alt="icon"
      className="nav-icon"
      onError={ ({ currentTarget }) => {
        currentTarget.onerror = null;
        currentTarget.src = DEFAULT_NAV_ICON;
      } }
    />
  );

  const isCollapsed = !isMobileViewport && collapsed;

  const getNavIcon = (item, type) => {
    const key = (item.navIconKey || item.module_key).replace(/-/g, "");
    let icon = null;

    if (type === "subMenu") {
      icon = renderSvgNavIcon(key);
    }
    if (type === "menu") {
      if (item.code !== undefined) {
        icon = item.icon === "+" ? <PlusOutlined className="nav-icon" /> : item.icon === '-' ? <MinimizeOutlined className="nav-icon" /> : item.icon;
      } else {
        icon = renderSvgNavIcon(key);
      }
    }

    if (!icon) return null;

    const count = getNavNotificationCount(item);
    if (!isCollapsed || !count) return icon;

    return (
      <Badge
        className="nav-icon-notification-badge"
        size="small"
        count={ count }
        overflowCount={ 99 }
        color="#FF4D4F"
      >
        <span className="nav-icon-badge-anchor">{ icon }</span>
      </Badge>
    );
  };

  const getModuleKey = (item) => (item.module_key === 'dashboard' ? '' : item.module_key);

  // ====================== FORMAT PERMISSIONS (pure - no side effects, no mutation of source data) ======================
  const formatPermissions = useCallback((data) => {
    if (!data) return [];

    const result = buildNavigationTree(data, navigation.custom_pay_items);
    const filteredResult = filterNavigationTree(result, menuFilter, translateNav);
    return renderMenuItems(filteredResult);
  }, [
    navigation.custom_pay_items,
    menuFilter,
    notificationModules,
    collapsed,
    currentModuleKey,
    countNotify,
    getNavNotificationCount,
    translateNav,
  ]);

  function renderMenuItems(data) {
    return data.map((item) => {
      const url = getUrl(item.sref);
      const itemKey = `${url}|${getModuleKey(item)}`;
      const menuKey = item.navMenuKey || itemKey;
      const isActive = currentModuleKey === getModuleKey(item);

      if (item.children && item.children.length > 0) {
        return (
          <SubMenu
            key={ menuKey }
            title={
              <div className="nav-item-content">
                <span className="nav-item-text">{ translateNav(item) }</span>
                <span className="nav-item-count">{ countNotify(item) }</span>
              </div>
            }
            icon={ getNavIcon(item, 'subMenu') }
            className={ isActive ? 'ant-menu-submenu-active-custom' : '' }
          >
            { renderMenuItems(item.children) }
          </SubMenu>
        );
      }

      let cleanKey = itemKey.replace("//", "");
      if (cleanKey === 'dashboard|') cleanKey = '';
      const finalPath = `/${cleanKey.replace(/\|(.*)/, "")}`;

      return (
        <Menu.Item
          key={ menuKey }
          title={ translateNav(item) }
          modulekey={ getModuleKey(item) }
          navpath={ finalPath }
          originitemkey={ itemKey }
          icon={ getNavIcon(item, "menu") }
        >
          <a
            href={ finalPath }
            style={ { color: 'inherit', display: 'block', width: '100%', textDecoration: 'none' } }
            onClick={ (e) => {
              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
              }
            } }
          >
            <div className="nav-item-content">
              <span className="nav-item-text">{ translateNav(item) }</span>
              <span className="nav-item-count">{ countNotify(item) }</span>
            </div>
          </a>
        </Menu.Item>
      );
    });
  }

  // ====================== MEMOIZED MENU (biggest performance win) ======================
  const renderedMenuItems = useMemo(() => {
    return formatPermissions(navigation.permissions);
  }, [formatPermissions, navigation.permissions, notificationModules, collapsed, currentModuleKey]);

  const handleOpenChange = (keys) => {
    if (keys?.length) {
      const lastKey = keys[keys.length - 1];
      setOpenKeys(lastKey && lastKey.includes('/') ? keys : [lastKey]);
    } else {
      setOpenKeys([]);
    }
  };

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const setCurrentModuleKey = (modulekey) => {
    setModuleKey(modulekey);
    localStorage.setItem('modulekey', modulekey);
    dispatch(clearFilterOptions());
  };

  function getLastSubstring(path) {
    const parts = path.split('/');
    return parts.pop();
  }

  const onClickMenu = (key, itemProps) => {
    let cleanKey = (itemProps?.originitemkey || key).replace("//", "");
    if (cleanKey === 'help_center') {
      window.open('http://help.klokhr.com', '_blank');
      return;
    }
    if (cleanKey === 'dashboard|') cleanKey = '';

    const modulekey = itemProps?.modulekey ?? getModuleKeyFromMenuKey(cleanKey);
    const navPath = itemProps?.navpath || `/${cleanKey.replace(/\|(.*)/, "")}`;

    setCurrentModuleKey(modulekey);
    navigate(navPath);
    setCurrent(cleanKey.replace(/\|(.*)/, ""));

    if (isMobileViewport) {
      setMobileNavOpen?.(false);
    }
  };

  // ====================== SAVE MENU STATE TO LOCALSTORAGE (separate effects - no longer inside getNavWithPermissions) ======================
  useEffect(() => {
    localStorage.setItem('selectedMenuItem', current);
  }, [current]);

  useEffect(() => {
    localStorage.setItem('openSubMenuKeys', JSON.stringify(openKeys));
  }, [openKeys]);

  useEffect(() => {
    localStorage.setItem('navThemeMode', navThemeMode);
  }, [navThemeMode]);

  const toggleNavThemeMode = () => {
    setNavThemeMode((currentMode) => (currentMode === 'dark' ? 'light' : 'dark'));
  };

  const sidebarContent = (
    <>
      <div className="sidebar-brand">
        <div className="sidebar-brand__identity">
          { isCollapsed ? (
            <div className="sidebar-brand__compact-logo">
            </div>
          ) : (
            <div className="sidebar-brand__content">
              <div className="sidebar-brand__logo-wrap">
                <img src={ require('@assets/images/image170.png') } alt="logo" className="sidebar-brand__logo-mark" />
              </div>
              <div className="sidebar-brand__copy">
                <img src={ require('@assets/images/KLOK.png') } alt="logo" className="sidebar-brand__logo-text" />
                <span className="sidebar-brand__subtitle">Smart HR Operations</span>
              </div>
            </div>
          ) }
        </div>

        <div className="sidebar-brand__actions">
          {
            !isCollapsed && <div
              onClick={ toggleNavThemeMode }
              className="sidebar-brand__toggle sidebar-brand__toggle--theme"
            >
              <Tooltip title={ navThemeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode' } placement="right">
                { navThemeMode === 'dark'
                  ? <BulbOutlined style={ { color: '#FF9800', fontSize: 16 } } />
                  : <MoonOutlined style={ { color: '#2F6BFF', fontSize: 16 } } />
                }
              </Tooltip>
            </div>
          }

          { !isMobileViewport ? (
            <div
              onClick={ handleCollapse }
              className="sidebar-brand__toggle"
            >
              <Tooltip title={ isCollapsed ? 'Maximize Menu' : "Minimize Menu" } placement="right">
                { isCollapsed ? <MenuUnfoldOutlined style={ { color: '#2F6BFF', fontSize: 16 } } /> : <MenuFoldOutlined style={ { color: '#2F6BFF', fontSize: 16 } } /> }
              </Tooltip>
            </div>
          ) : null }
        </div>
      </div>

      <Spin spinning={ loading }>
        { !isCollapsed && (
          <div className="sidebar-tools__panel">
            <Input
              ref={ searchModuleRef }
              allowClear
              value={ menuFilter }
              onChange={ (event) => setMenuFilter(event.target.value) }
              placeholder="Search Menu... Ctrl + M"
              className="sidebar-tools__filter"
            />
            <div className="sidebar-tools__meta">
              { menuFilter ? <span>Filtering active</span> : '' }
            </div>
          </div>
        ) }

        <div className={ `menucontainer${isCollapsed ? ' menucontainer--collapsed' : ''}` }>
          <Menu
            mode="inline"
            theme={ theme }
            selectedKeys={ getSelectedMenuKeys(current, currentModuleKey) }
            openKeys={ isCollapsed ? undefined : openKeys }
            onOpenChange={ handleOpenChange }
            onClick={ ({ key, item }) => onClickMenu(key, item?.props) }
            triggerSubMenuAction={ isCollapsed ? "hover" : "click" }
            subMenuOpenDelay={ 0.05 }
            subMenuCloseDelay={ 0.12 }
          >
            { renderedMenuItems }
          </Menu>
        </div>
      </Spin>
    </>
  );

  return (
    <>
      { isMobileViewport ? (
        <Drawer
          placement="left"
          open={ mobileNavOpen }
          onClose={ () => setMobileNavOpen?.(false) }
          closable={ false }
          width={ 320 }
          className={ `app-navigation-drawer app-navigation-drawer--${navThemeMode}` }
          rootClassName={ `app-navigation-drawer-root app-navigation-drawer-root--${navThemeMode}` }
          bodyStyle={ { padding: 0, background: 'transparent' } }
        >
          <div className={ `app-navigation-drawer__inner app-navigation-drawer__inner--${navThemeMode}` }>
            { sidebarContent }
          </div>
        </Drawer>
      ) : (
        <Sider width={ 244 } collapsedWidth={ 72 } trigger={ null } collapsible collapsed={ collapsed } theme="light" className={ `app-navigation-sider app-navigation-sider--${navThemeMode}` }>
          { sidebarContent }
        </Sider>
      ) }
    </>
  );
};

export default NavSide;

function getModuleKeyFromMenuKey(key = '') {
  const [, modulekey = ''] = String(key).split('|');
  return modulekey;
}

function getSelectedMenuKeys(current, currentModuleKey) {
  const baseKey = `${current}|${currentModuleKey}`;
  return [baseKey, `${baseKey}${REQUEST_GROUP_MENU_KEY_SUFFIX}`, `${baseKey}${APPROVAL_GROUP_MENU_KEY_SUFFIX}`];
}

function buildNavigationTree(data, customPayItems = []) {
  if (!data) return [];

  const result = [];
  const map = new Map();

  data.forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  map.forEach((item) => {
    if (item.parentId === null) {
      result.push(item);
    } else {
      const parent = map.get(item.parentId);
      if (parent) {
        parent.children.push(item);
      }

      if (item.module_key === "customPayitem" && customPayItems?.length > 0) {
        customPayItems.forEach((originalEach) => {
          item.children.push({
            ...originalEach,
            sref: `app.payrollmanagement.custom_payitem.item/${originalEach.name}/${originalEach.id}`,
            module_key: `${originalEach.name}_custom_payitem`,
            hasPermisson: true,
            icon: originalEach.type === 'deduction' ? '-' : originalEach.type === 'earning' ? '+' : originalEach.type === 'benefit' ? 'B. ' : '',
            parentId: item.id,
            tags: `payitem${originalEach.name}`,
            children: [],
          });
        });
      }
    }
  });

  return injectSyntheticParents(result);
}

function injectSyntheticParents(items = []) {
  let result = [...items];
  result = injectSyntheticParent(result, {
    module_key: 'requestsGroup',
    name: 'Requests',
    navMenuKey: 'requests-group|requestsGroup',
    navIconKey: 'attendanceRequests',
    insertAfterModuleKey: 'dashboard',
    suffix: REQUEST_GROUP_MENU_KEY_SUFFIX,
    groupType: GROUP_TYPES.requests,
    matcher: (item) => REQUEST_GROUP_MODULE_KEYS.includes(item.module_key),
  });
  result = injectSyntheticParent(result, {
    module_key: 'approvalsGroup',
    name: 'Approvals',
    navMenuKey: 'approvals-group|approvalsGroup',
    navIconKey: 'attendanceApprovals',
    insertAfterModuleKey: 'requestsGroup',
    suffix: APPROVAL_GROUP_MENU_KEY_SUFFIX,
    groupType: GROUP_TYPES.approvals,
    matcher: (item) => item.module_key?.toLowerCase().includes('approval'),
  });
  return result;
}

function injectSyntheticParent(items = [], config) {
  const { module_key, name, navMenuKey, navIconKey, insertAfterModuleKey, suffix, groupType, matcher } = config;
  const children = collectGroupedMenuItems(items, matcher, suffix);
  if (children.length === 0) {
    return items;
  }

  const syntheticParent = {
    id: `${module_key}-parent`,
    module_key,
    name,
    sref: null,
    parentId: null,
    navMenuKey,
    navIconKey,
    syntheticGroup: true,
    groupType,
    children,
  };

  const insertAfterIndex = items.findIndex((item) => item.module_key === insertAfterModuleKey);
  if (insertAfterIndex === -1) {
    return [...items, syntheticParent];
  }

  return [
    ...items.slice(0, insertAfterIndex + 1),
    syntheticParent,
    ...items.slice(insertAfterIndex + 1),
  ];
}

function collectGroupedMenuItems(items = [], matcher, suffix) {
  const collectedItems = [];

  const traverse = (nodes) => {
    nodes.forEach((item) => {
      if (!item.syntheticGroup && matcher(item)) {
        collectedItems.push({
          ...item,
          children: item.children ? [...item.children] : [],
          navIconKey: getGroupedMenuIconKey(item),
          navMenuKey: `${getUrlFromSref(item.sref)}|${item.module_key}${suffix}`,
        });
      }

      if (item.children?.length > 0) {
        traverse(item.children);
      }
    });
  };

  traverse(items);
  return collectedItems;
}

function getGroupedMenuIconKey(item) {
  return GROUPED_MENU_ICON_MAP[item.module_key] || item.navIconKey || item.module_key;
}

function getUrlFromSref(sref) {
  let url = sref ? sref.replace(/^app\./, '').replace(/_/g, '-').replace(/\./g, '/') : '';
  if (url === "masters/general/accolades") url = url.replace(/\/accolades/g, '');
  if (url === "masters/system/users") url = url.replace(/\/users/g, '');
  if (url === "payrollmanagement/payrolls/index") url = url.replace(/\/index/g, '');
  if (url === 'dashboard') url = '';
  return url;
}

function filterNavigationTree(items, query, translateNav) {
  const keyword = query.trim().toLowerCase();
  if (!keyword) {
    return items;
  }

  return items.reduce((acc, item) => {
    const children = filterNavigationTree(item.children || [], query, translateNav);
    const label = `${translateNav(item) || ''} ${item.module_key || ''} ${item.name || ''}`.toLowerCase();

    if (label.includes(keyword) || children.length > 0) {
      acc.push({
        ...item,
        children,
      });
    }

    return acc;
  }, []);
}
